import { change } from 'redux-form';
import _ from 'lodash';
import moment from 'moment';
import { initialize } from 'redux-form';

import {
  DATE_FORMAT_FOR_UI,
  DATE_FORMAT_FOR_DB_STRING,
  NOTIFICATION_DIALOG_MESSAGES,
} from 'Common/Constants';
import { loading, success, error } from 'State/actions';
import {
  APPLICATION_HUB_APP_INFO_FORM_NAME,
  APPLICATION_HUB_HEADER_FORM_NAME,
  APPLICATION_HUB_REPROCESS_MEALS_FORM_NAME,
} from 'Common/FormEnum';
import { ApplicationFieldEnum as Fields } from 'Common/FieldEnum';
import { getControlInfo } from 'State/controlInfo/actions';
import { createToast, toastType } from 'Common/ToastHelper';
import { Api } from 'API/MPWApi';
import {
  requestAccess,
  resetAccessPrompt,
} from 'Common/Components/AccessPrompt/state/actions';
import { getIsDayClosed } from 'Common/GeneralHelper';
import { setNotificationDialog } from 'Components/dialogs/state/actions';
import { getReprocessTransactions } from 'State/selectors';
import { deleteIncomingApplication } from 'Pages/reviewincomingapplications/state/grid/actions';
import { getCaseTypes } from 'State/caseTypes/actions';
import * as PageStateCacheActions from 'State/pagestatecache/actions';
import { PAGE_STATE_CACHES } from 'State/pagestatecache/helpers/Constants';

import { extractReprocessMealsModel } from '../../helpers/hub';
import { MANAGE_APPLICATION_LOADING } from '../manageapplication/types';
import * as types from './types';

export const fetchApplication = (familyId, isCopying = false) => {
  return async (dispatch, getState, MPWWebApi) => {
    dispatch(loading(types.APPLICATION_HUB_FETCHING, true));

    if (isCopying) {
      dispatch(loading(MANAGE_APPLICATION_LOADING, true));
    }

    try {
      await dispatch(getControlInfo(false));
      const response = await MPWWebApi.getGeneric(
        `Application/${familyId}/${isCopying}`
      );
      await dispatch(getCaseTypes());
      const responseRelease = await MPWWebApi.getGeneric(
        'ControlInfo/ReleaseCategory'
      );

      if (_.has(responseRelease, 'data.Data')) {
        dispatch(
          success(types.APPLICATION_HUB_FETCH_RELEASE_CATEGORY, {
            data: responseRelease.data.Data,
          })
        );
      }

      if (_.has(response, 'data.Data')) {
        dispatch(
          success(types.APPLICATION_HUB_FETCH_APPLICATION, {
            isCopying,
            Data: response.data.Data,
          })
        );
      }
    } catch (ex) {
      dispatch(error(types.APPLICATION_HUB_FETCH_ERROR, ex));
    }

    dispatch(loading(types.APPLICATION_HUB_FETCHING, false));

    if (isCopying) {
      dispatch(loading(MANAGE_APPLICATION_LOADING, false));
    }
  };
};

/**
 * @param {Object} data
 * @param {Array<String>} data.StudentIdSeeds
 * @param {Date} data.FromDate
 * @param {Date} data.ToDate
 * @param {Integer} passNumber
 */
export const checkClosedDates = (data, passNumber = 1) => async (
  dispatch,
  getState,
  MPWWebApi
) => {
  try {
    dispatch(loading(types.APPLICATION_HUB_WRITE_REPROCESS_LOADING, true));

    const reprocessMealModels = data.StudentIdSeeds.map((StudentIdSeed) => ({
      StudentIdSeed,
      FromDate: data.FromDate,
      ToDate: data.ToDate,
    }));

    const values = await Promise.all(
      reprocessMealModels.map((model) =>
        MPWWebApi.postGenericInsertUpdate(
          'Application/GetReimbursableTransactions',
          model
        ).then((response) => response.data.Data)
      )
    );

    const siteIds = _.uniq(
      _.flattenDeep(values).map((transaction) => transaction.PrimarySiteId)
    );

    const siteId = siteIds[passNumber - 1];

    const formattedDate = moment().format(DATE_FORMAT_FOR_DB_STRING);

    const isBLClosed = await getIsDayClosed({
      PrimarySiteId: siteId,
      ClosingDate: formattedDate,
      Supper: false,
    });

    const isSupperClosed = await getIsDayClosed({
      PrimarySiteId: siteId,
      ClosingDate: formattedDate,
      Supper: true,
    });

    const closed = isSupperClosed || isBLClosed;
    const goToNextStep = siteIds.length <= passNumber;

    if (closed) {
      const dateToClose = moment().format(DATE_FORMAT_FOR_UI);

      const siteClosedDateString = `${isBLClosed ? 'B/L/O' : ''}${
        isSupperClosed && isBLClosed ? ' and ' : ''
      }${isSupperClosed ? 'Supper' : ''} on ${dateToClose} at site ${siteId}`;

      const actionsToDispatchOnCancel = [
        loading(types.APPLICATION_HUB_WRITE_REPROCESS_LOADING, false),
      ];

      const actionsToDispatchOnOk = [
        async () => {
          try {
            if (isBLClosed) {
              await MPWWebApi.postGenericInsertUpdate(
                `uncloseday/WriteUncloseDays`,
                {
                  BeginDT: dateToClose,
                  EndDT: dateToClose,
                  IsSupper: false,
                  SiteId: siteId,
                  mealSummaries: [],
                }
              );
            }

            if (isSupperClosed) {
              await MPWWebApi.postGenericInsertUpdate(
                `uncloseday/WriteUncloseDays`,
                {
                  BeginDT: dateToClose,
                  EndDT: dateToClose,
                  IsSupper: false,
                  SiteId: siteId,
                  mealSummaries: [],
                }
              );
            }
            createToast(
              toastType.success,
              'Success',
              `${siteClosedDateString} unclosed successfully!`
            );

            if (goToNextStep) {
              dispatch(writeReprocessMeals(data));
            } else {
              dispatch(checkClosedDates(data, passNumber + 1));
            }
          } catch (error) {
            if (error.response.status === 401) {
              dispatch(
                requestAccess({
                  showCancel: false,
                  closeOnUser: false,
                  onUser: async (user) => {
                    try {
                      const UserApi = new Api(user.Token);
                      if (isBLClosed) {
                        await UserApi.postGenericInsertUpdate(
                          `uncloseday/WriteUncloseDays`,
                          {
                            BeginDT: dateToClose,
                            EndDT: dateToClose,
                            IsSupper: false,
                            SiteId: siteId,
                            mealSummaries: [],
                          }
                        );
                      }

                      if (isSupperClosed) {
                        await UserApi.postGenericInsertUpdate(
                          `uncloseday/WriteUncloseDays`,
                          {
                            BeginDT: dateToClose,
                            EndDT: dateToClose,
                            IsSupper: false,
                            SiteId: siteId,
                            mealSummaries: [],
                          }
                        );
                      }
                      createToast(
                        toastType.success,
                        'Success',
                        `${siteClosedDateString} unclosed successfully!`
                      );

                      dispatch(resetAccessPrompt());
                      if (goToNextStep) {
                        dispatch(writeReprocessMeals(data));
                      } else {
                        dispatch(checkClosedDates(data, passNumber + 1));
                      }
                    } catch (e) {
                      createToast(
                        toastType.error,
                        'Error',
                        'You do not have access to unclose the day.'
                      );
                    }
                  },
                  title: `Please enter credentials to unclose day for ${siteClosedDateString}`,
                })
              );
            }
          }
        },
      ];

      dispatch(
        setNotificationDialog({
          title: `${siteClosedDateString} ${
            isSupperClosed && isBLClosed ? 'are' : 'is'
          } closed, would you like to unclose to reprocess meals?`,
          message: 'The day(s) will be unclosed and meals will be reprocessed.',
          showCancel: true,
          actionsToDispatchOnOk,
          actionsToDispatchOnCancel,
        })
      );
    } else {
      dispatch(writeReprocessMeals(data));
    }
  } catch (ex) {
    dispatch(error(types.APPLICATION_HUB_FETCH_ERROR, ex));
  }
};

/**
 * @param {Object} data
 * @param {Array<String>} data.StudentIdSeeds
 * @param {Date} data.FromDate
 * @param {Date} data.ToDate
 */
export const checkReprocessMeals = (data) => async (
  dispatch,
  getState,
  MPWWebApi
) => {
  try {
    const response = await MPWWebApi.postGenericInsertUpdate(
      'Application/GetCheckReprocessMeals',
      data
    );

    const hasMeals = response.data.Data;
    dispatch(setHasReimbursableMeals(hasMeals));
    if (hasMeals) {
      dispatch(
        setNotificationDialog({
          ...NOTIFICATION_DIALOG_MESSAGES.APPLICATION_HUB_REPROCESS_MEALS,
          actionsToDispatchOnOk: [checkClosedDates(data, 1)],
        })
      );
    }
  } catch (ex) {
    dispatch(error(types.APPLICATION_HUB_FETCH_ERROR, ex));
  }
};

/**
 * @param {Object} data
 * @param {Array<String>} data.StudentIdSeeds
 * @param {Date} data.FromDate
 * @param {Date} data.ToDate
 */
export const writeReprocessMeals = (data) => async (
  dispatch,
  getState,
  MPWWebApi
) => {
  try {
    dispatch(loading(types.APPLICATION_HUB_WRITE_REPROCESS_LOADING, true));
    const response = await MPWWebApi.postGenericInsertUpdate(
      'Application/WriteReprocessMeals',
      data
    );

    const message = response.data.Data;
    dispatch(setReprocessSuccessful(message));
  } catch (ex) {
    dispatch(error(types.APPLICATION_HUB_FETCH_ERROR, ex));
  } finally {
    dispatch(loading(types.APPLICATION_HUB_WRITE_REPROCESS_LOADING, false));
  }
};

export const setHasReimbursableMeals = (hasReimbursableMeals) => ({
  type: types.APPLICATION_HUB_SET_HAS_REIMBURSABLE_MEALS,
  payload: hasReimbursableMeals,
});

export const resetHasReimbursableMeals = () => ({
  type: types.APPLICATION_HUB_RESET_HAS_REIMBURSABLE_MEALS,
});

export const setReprocessSuccessful = (successful) => ({
  type: types.APPLICATION_HUB_WRITE_REPROCESS_SAVE_SUCCESSFUL,
  payload: successful,
});

export const submitApplication = (incomingAppId) => {
  return async (dispatch, getState, MPWWebApi) => {
    dispatch(loading(types.APPLICATION_HUB_SUBMITTING, true));

    try {
      const state = getState();
      const { tempApplication } = state.ManageApplicationState.hub;

      const response = await MPWWebApi.postGenericInsertUpdate(
        'Application/Write',
        tempApplication
      );

      if (_.has(response, 'data.Data.FamilyNumber')) {
        var familyId = response.data.Data.FamilyNumber;
        const readApp = await MPWWebApi.getGeneric(`Application/${familyId}`);
        const hubData = readApp.data.Data;

        dispatch(success(types.APPLICATION_HUB_SUBMITTED, hubData));

        // gets user preference for reprocessing transactions
        const reprocessTransactions = getReprocessTransactions(state);

        if (reprocessTransactions) {
          const reprocessMealsModel = extractReprocessMealsModel(hubData);
          dispatch(checkReprocessMeals(reprocessMealsModel));
          dispatch(
            initialize(
              APPLICATION_HUB_REPROCESS_MEALS_FORM_NAME,
              reprocessMealsModel
            )
          );
        }

        if (!_.isNil(incomingAppId)) {
          dispatch(deleteIncomingApplication(incomingAppId));
          dispatch(
            PageStateCacheActions.removePageState(
              PAGE_STATE_CACHES.ReviewIncomingApps
            )
          );
        }
      }
    } catch (ex) {
      dispatch(error(types.APPLICATION_HUB_SUBMIT_ERROR, ex));
    }

    dispatch(loading(types.APPLICATION_HUB_SUBMITTING, false));
  };
};

export const getUpdatedApplication = () => {
  return async (dispatch, getState, MPWWebApi) => {
    dispatch(loading(types.APPLICATION_HUB_FETCHING, true));
    try {
      const {
        tempApplication,
        originalStatusCurrent,
        originalStatusFuture,
        hasStatusChanged,
      } = getState().ManageApplicationState.hub;

      const statusResponse = await MPWWebApi.postGenericInsertUpdate(
        'application/RecalculateApplication',
        tempApplication
      );

      if (_.has(statusResponse, 'data.Data')) {
        dispatch(
          success(types.APPLICATION_HUB_FETCH_UPDATED_APP, {
            data: statusResponse.data.Data,
          })
        );
        if (statusResponse.data.Data.Students.length > 0) {
          if (
            originalStatusCurrent !==
              statusResponse.data.Data.Students[0].StatusCurrent ||
            originalStatusFuture !==
              statusResponse.data.Data.Students[0].StatusFuture
          ) {
            dispatch(setHasStatusChanged(true));
          }
          if (
            hasStatusChanged &&
            originalStatusCurrent ===
              statusResponse.data.Data.Students[0].StatusCurrent &&
            originalStatusFuture ===
              statusResponse.data.Data.Students[0].StatusFuture
          ) {
            dispatch(setHasStatusChanged(false));
          }
        }
        dispatch(updateFamilySize(statusResponse.data.Data));
      }

      dispatch(setAppRecalculated(true));
    } catch (ex) {
      dispatch(error(types.APPLICATION_HUB_FETCH_ERROR, ex));
    }
    dispatch(loading(types.APPLICATION_HUB_FETCHING, false));
  };
};

export const writeVerifyAction = (householdSystemId, actionCode) => {
  return async (dispatch, getState, MPWWebApi) => {
    dispatch(loading(types.APPLICATION_HUB_FETCHING, true));

    try {
      const response = await MPWWebApi.postGenericInsertUpdate(
        'Verification/WriteVerifyAction',
        {
          Action: actionCode,
          HouseholdSystemId: householdSystemId,
        }
      );

      dispatch(redirectFromHub());
      if (response.status === 200) {
        createToast(
          toastType.success,
          'Success',
          'Application successfully updated.'
        );
      }
    } catch (error) {
      dispatch({
        type: types.APPLICATION_HUB_FETCH_ERROR,
        payload: error.response,
      });
    }

    dispatch(loading(types.APPLICATION_HUB_FETCHING, false));
  };
};

export const updateHOH = (hoh) => {
  return {
    type: types.APPLICATION_HUB_UPDATE_HOH,
    payload: hoh,
  };
};

export const updateMember = (member) => {
  return {
    type: types.APPLICATION_HUB_UPDATE_MEMBER,
    payload: member,
  };
};

export const updateStudent = (student, isNewStudent, previousId) => {
  return {
    type: types.APPLICATION_HUB_UPDATE_STUDENT,
    payload: { student, isNewStudent, previousId },
  };
};

export const deleteMember = (member) => {
  return (dispatch) => {
    dispatch({
      type: types.APPLICATION_HUB_DELETE_MEMBER,
      payload: member,
    });
  };
};

export const deleteStudent = (studentId) => {
  return (dispatch) => {
    dispatch({
      type: types.APPLICATION_HUB_DELETE_STUDENT,
      payload: studentId,
    });
  };
};

export const updateFamilySize = (tempApplication) => {
  return {
    type: types.APPLICATION_HUB_UPDATE_APPSUMMARY_FAMILYSIZE,
    payload: tempApplication,
  };
};

export const setAppRecalculated = (value) => {
  return {
    type: types.APPLICATION_HUB_APP_RECALCULATED,
    payload: value,
  };
};

export const forceRecalculate = () => {
  return {
    type: types.APPLICATION_HUB_FORCE_RECALCULATE,
  };
};

export const changeApplicationDate = (date, recalculate) => {
  return {
    type: types.APPLICATION_HUB_CHANGE_APPLICATION_DATE,
    payload: { date, recalculate },
  };
};

export const changeReleaseCategories = (value) => {
  return change(
    APPLICATION_HUB_APP_INFO_FORM_NAME,
    Fields.ReleaseMapping,
    value
  );
};

export const clearCaseInfo = () => {
  return {
    type: types.APPLICATION_HUB_CLEAR_CASE_INFO,
  };
};

export const clearHub = () => {
  return {
    type: types.APPLICATION_HUB_CLEAR,
  };
};

export const removeIncome = (id) => {
  return {
    type: types.APPLICATION_HUB_REMOVE_INCOME,
    payload: id,
  };
};

export const clearErrors = () => {
  return {
    type: types.APPLICATION_HUB_CLEAR_ERRORS,
  };
};

export const changeEffectiveDate = (id, date) => {
  return {
    type: types.APPLICATION_HUB_CHANGE_EFFECTIVE_DATE,
    payload: { id, date },
  };
};

export const redirectFromHub = () => {
  return {
    type: types.APPLICATION_HUB_REDIRECT,
    payload: null,
  };
};

export const setIsSecondReview = (isSecondReview) => {
  return {
    type: types.APPLICATION_HUB_SET_IS_SECOND_REVIEW,
    payload: isSecondReview,
  };
};

export const setOriginalStatuses = (
  originalStatusCurrent,
  originalStatusFuture
) => {
  return {
    type: types.APPLICATION_HUB_SET_ORIGINAL_STATUSES,
    payload: { originalStatusCurrent, originalStatusFuture },
  };
};

export const setShowSecondReviewStatusChangeDialog = (isShown) => {
  return {
    type: types.APPLICATION_HUB_SET_SHOW_SECOND_REVIEW_STATUS_CHANGE_DIALOG,
    payload: isShown,
  };
};

export const setHasStatusChanged = (hasChanged) => {
  return {
    type: types.APPLICATION_HUB_SET_HAS_STATUS_CHANGED,
    payload: hasChanged,
  };
};

export const setSecondReviewResultRecorded = (hasBeenRecorded) => {
  return {
    type: types.APPLICATION_HUB_SET_SECOND_REVIEW_RESULT_RECORDED,
    payload: hasBeenRecorded,
  };
};
export const resetSecondReviewResult = (field) => {
  return change(APPLICATION_HUB_HEADER_FORM_NAME, field, false);
};
export const setIsVerification = (isVerification) => {
  return {
    type: types.APPLICATION_HUB_SET_IS_VERIFICATION,
    payload: isVerification,
  };
};
export const setIsSelectedForVerification = (isSelectedForVerification) => {
  return {
    type: types.APPLICATION_HUB_SET_IS_SELECTED_FOR_VERIFICATION,
    payload: isSelectedForVerification,
  };
};

export const setVerificationSelectionLetterPrinted = (
  verificationSelectionLetterPrinted
) => {
  return {
    type: types.APPLICATION_HUB_SET_VERIFICATION_SELECTION_LETTER_PRINTED,
    payload: verificationSelectionLetterPrinted,
  };
};



// WEBPACK FOOTER //
// ./src/pages/manageapplication/state/hub/actions.js